/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';

import { getSelectedProductIdFromActionInput } from '@msdyn365-commerce-modules/retail-actions';
import { AttributeDataType, IQueryResultSettings, ProductRefinerValue, ProductsDataActions, ProductSearchCriteria, ProductSearchResult } from '@msdyn365-commerce/retail-proxy';
import { searchByCriteriaAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { addAlcoholRefiner, addAllAttributeRefiners, addOrUpdateZeroPriceRefiner, shouldFilterAlcohol } from '../modules/dlvry-search-result-container/actions/refiner-utils';
import { applyAttributesToProduct, getAttributeNumberOrDefault } from '../shared-utils/attributes';

/**
 * GetAlternativeProducts Input Action
 */
export class GetAlternativeProductsInput implements Msdyn365.IActionInput {

    public productId: number;
    public useExperimentalAlternativeProducts?: boolean;

    constructor(productId: number, useExperimentalAlternativeProducts?: boolean) {
        this.productId = productId;
        this.useExperimentalAlternativeProducts = useExperimentalAlternativeProducts;
    }

    public getCacheKey = () => `AlternativeProducts-${this.productId}`;
    public getCacheObjectType = () => 'ProductSearchResult';
    public dataCacheType = (): Msdyn365.CacheType => 'application';
}

interface ActionConfig {
    useExperimentalAlternativeProducts?: boolean;
}

const createInput = (args: Msdyn365.ICreateActionContext<ActionConfig>): Msdyn365.IActionInput => {
    const productId = getSelectedProductIdFromActionInput(args);

    if (productId) {
        return new GetAlternativeProductsInput(+productId, args.config?.useExperimentalAlternativeProducts);
    }

    throw new Error('Unable to create GetAlternativeProductsInput: productId is not defined.');
};

async function getProductPriority(ctx: Msdyn365.IActionContext, productId: number): Promise<number | undefined> {
    const result = await searchByCriteriaAsync({
        callerContext: ctx
    }, {
        Context: {
            CatalogId: ctx.requestContext.apiSettings.catalogId,
            ChannelId: ctx.requestContext.apiSettings.channelId
        },
        Ids: [productId],
        IncludeAttributes: true,
        SkipVariantExpansion: true
    });
    const product = result && result.length > 0 ? result[0] : undefined;

    if (!product || !product.AttributeValues) {
        return undefined;
    }

    return getAttributeNumberOrDefault(ctx.requestContext.locale, product.AttributeValues, 'priority', undefined as unknown as number);
}

async function getProductCategoryId(ctx: Msdyn365.IActionContext, productId: number): Promise<number | undefined> {
    const paths = await ProductsDataActions.getCategoryPathsAsync(
        { callerContext: ctx },
        ctx.requestContext.apiSettings.channelId,
        ctx.requestContext.apiSettings.catalogId,
        [{ ProductId: productId }]
    );
    const lastSegment = paths.find(x => x.ProductId === productId)?.CategoryPath?.[0]?.Segments?.pop();

    return lastSegment ? lastSegment.CategoryId : undefined;
}

function getMinPriority(priority: number): string {
    if (priority >= 1000) {
        return '500';
    }
    if (priority >= 500) {
        return '500';
    }
    if (priority >= 0) {
        return '0';
    }
    return '0';
}

async function action(input: GetAlternativeProductsInput, ctx: Msdyn365.IActionContext): Promise<ProductSearchResult[]> {
    if (input.useExperimentalAlternativeProducts === true) {
        return [];
    }

    const prioritySortingAttribute = ctx.requestContext.app.config?.prioritySortingAttribute as string | undefined;

    // Priority sorting attribute must be set and a valid number. If not return empty array
    if (!prioritySortingAttribute || isNaN(+prioritySortingAttribute)) {
        return [];
    }

    const [categoryId, priority] = await Promise.all([
        getProductCategoryId(ctx, input.productId),
        getProductPriority(ctx, input.productId)
    ]);

    if (!categoryId) {
        return [];
    }

    const items = 12;
    const searchCriteriaInput: ProductSearchCriteria = {
        Context: {
            CatalogId: ctx.requestContext.apiSettings.catalogId,
            ChannelId: ctx.requestContext.apiSettings.channelId
        },
        CategoryIds: [categoryId],
        IncludeAttributes: true,
        SearchCondition: '',
        SkipVariantExpansion: true
    };
    const queryResultSettings: IQueryResultSettings = {
        count: true,
        Paging: {
            // +1 so that we can hide the current product in the results. If product is not in the results, we will just ignore the extra item
            Top: items + 1
        }
    };

    searchCriteriaInput.Refinement = addOrUpdateZeroPriceRefiner(ctx, searchCriteriaInput.Refinement);
    if (shouldFilterAlcohol(ctx)) {
        await addAlcoholRefiner(ctx, searchCriteriaInput);
    }

    await addAllAttributeRefiners(ctx, searchCriteriaInput);

    queryResultSettings.Sorting = queryResultSettings.Sorting ?? {};
    queryResultSettings.Sorting.Columns = queryResultSettings.Sorting.Columns ?? [];
    const priorityColumnName = `Attr_${prioritySortingAttribute}`;
    const isPriorityAttributeExist = queryResultSettings.Sorting.Columns.some(column => column.ColumnName === priorityColumnName);
    if (!isPriorityAttributeExist) {
        queryResultSettings.Sorting.Columns.push({
            ColumnName: priorityColumnName,
            IsDescending: true
        });
    }

    const priorityRefiner: ProductRefinerValue = {
        DataTypeValue: AttributeDataType.Integer,
        LeftValueBoundString: getMinPriority(priority || 0),
        RefinerRecordId: +prioritySortingAttribute,
        RefinerSourceValue: 1,
        RightValueBoundString: '9999',
        UnitText: ''
    };
    searchCriteriaInput.Refinement.push(priorityRefiner);

    let products = await searchByCriteriaAsync({
        callerContext: ctx,
        queryResultSettings
    }, searchCriteriaInput);

    if (products && products.length > 0) {
        if (products.find(x => x.RecordId === input.productId)) {
            // Remove the current product from the results
            products = products.filter(x => x.RecordId !== input.productId);
        } else {
            products = products.slice(0, items);
        }
    }

    for (const product of products) {
        applyAttributesToProduct(ctx.requestContext, product, product.AttributeValues, undefined);
    }

    return products;
}

export default Msdyn365.createObservableDataAction({
    action: <Msdyn365.IAction<ProductSearchResult[]>>action,
    id: 'GetAlternativeProducts',
    input: createInput
});
